import React, { useState, useEffect } from 'react';
import "./FormSettings.scss";

const SettingsPopup = ({ isOpen, onClose, businessProfile, onSaveSuccess }) => {
    const [formData, setFormData] = useState({
        owner_name: '',
        business_name: '',
        slug: '',
        business_type: '',
        email: '',
        phone_number: '',
        address: '',
        city: '',
        twillio_number: '',
        country: '',
        hero_text: '',
        description: '',
        charge_apply_if_order_less_than_amount: 0,
        emergency_contact: '',
        collection_allowed: true,
        delivery_allowed: true,
        delivery_charge: true,
        charge_delivery_price: 0,
        ai_persona_name: '',
        ai_gender: '',
        ai_tone: '',
        web_address: '',
        // SMS Settings
        send_sms_create_booking_owner: false,
        send_sms_create_booking_customer: false,
        send_sms_accept_booking_owner: false,
        send_sms_accept_booking_customer: false,
        send_sms_cancel_booking_owner: false,
        send_sms_cancel_booking_customer: false,
        send_sms_reschedule_booking_owner: false,
        send_sms_reschedule_booking_customer: false,
        send_sms_complaint_receipt_owner: false,
        send_sms_complaint_receipt_customer: false
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [activeTab, setActiveTab] = useState('business');

    const businessTypes = [
        'Restaurant',
        'Take Away',
        'Retail Store',
        'Service Business',
        'Healthcare',
        'Consulting',
        'Manufacturing',
        'Technology',
        'Education',
        'Garage',
        'Other'
    ];

    const aiTones = [
        'Professional',
        'Friendly',
        'Formal',
        'Casual',
        'Enthusiastic',
        'Helpful'
    ];

    // Populate form when businessProfile prop changes
    useEffect(() => {
        if (businessProfile) {
            setFormData(prev => ({
                ...prev,
                owner_name: businessProfile.owner_name || '',
                business_name: businessProfile.business_name || '',
                business_type: businessProfile.business_type || '',
                email: businessProfile.email || '',
                phone_number: businessProfile.phone_number || '',
                address: businessProfile.physical_address || '',
                city: businessProfile.city || '',
                twillio_number: businessProfile.twillio_number || '',
                country: businessProfile.country || '',
                hero_text: businessProfile.hero_text || '',
                description: businessProfile.description || '',
                charge_apply_if_order_less_than_amount: businessProfile.charge_apply_if_order_less_than_amount || 0,
                emergency_contact: businessProfile.emergency_contact || '',
                slug: businessProfile.slug || '',
                collection_allowed: businessProfile.collection_allowed !== undefined
                    ? businessProfile.collection_allowed
                    : true,
                delivery_allowed: businessProfile.delivery_allowed !== undefined
                    ? businessProfile.delivery_allowed
                    : true,
                delivery_charge: businessProfile.delivery_charge !== undefined
                    ? businessProfile.delivery_charge
                    : true,
                charge_delivery_price: businessProfile.charge_delivery_price || 0,
                ai_persona_name: businessProfile.ai_persona_name || 'Assistant',
                ai_gender: businessProfile.ai_gender || 'male',
                ai_tone: businessProfile.ai_tone || 'Professional',
                web_address: businessProfile.web_address || '',
                // SMS Settings - Use data from smsSettings if available
                ...(businessProfile.smsSettings && businessProfile.smsSettings.length > 0 && businessProfile.smsSettings[0]
                    ? {
                        send_sms_create_booking_owner: businessProfile.smsSettings[0].send_sms_create_booking_owner || false,
                        send_sms_create_booking_customer: businessProfile.smsSettings[0].send_sms_create_booking_customer || false,
                        send_sms_accept_booking_owner: businessProfile.smsSettings[0].send_sms_accept_booking_owner || false,
                        send_sms_accept_booking_customer: businessProfile.smsSettings[0].send_sms_accept_booking_customer || false,
                        send_sms_cancel_booking_owner: businessProfile.smsSettings[0].send_sms_cancel_booking_owner || false,
                        send_sms_cancel_booking_customer: businessProfile.smsSettings[0].send_sms_cancel_booking_customer || false,
                        send_sms_reschedule_booking_owner: businessProfile.smsSettings[0].send_sms_reschedule_booking_owner || false,
                        send_sms_reschedule_booking_customer: businessProfile.smsSettings[0].send_sms_reschedule_booking_customer || false,
                        send_sms_complaint_receipt_owner: businessProfile.smsSettings[0].send_sms_complaint_receipt_owner || false,
                        send_sms_complaint_receipt_customer: businessProfile.smsSettings[0].send_sms_complaint_receipt_customer || false
                    }
                    : {})
            }));
        }
    }, [businessProfile]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else if (type === 'number') {
            setFormData(prev => ({
                ...prev,
                [name]: parseFloat(value) || 0
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');

        if (!token) {
            throw new Error('No authentication token found. Please log in again.');
        }

        try {
            // Determine if we're updating or creating
            const url = businessProfile?.id
                ? `${process.env.REACT_APP_API_URL}/business/business-profile/${businessProfile.id}`
                : '/api/business-profile';

            const method = businessProfile?.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`  // Add Authorization header
                },
                body: JSON.stringify(formData)
            });

            // Check if the response is OK
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            setMessage({
                text: 'Settings saved successfully!',
                type: 'success'
            });

            // Notify parent component
            if (onSaveSuccess) {
                onSaveSuccess(data);
            }

            // Close popup after successful save
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({
                text: error.message || 'Failed to save settings. Please try again.',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className='settings-modal'>
            <div className="modal-backdrop" onClick={onClose}>
                <div className="modal-container poppin" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2 className="modal-title">
                            <i className="fas fa-cog me-2"></i>
                            Business Settings
                        </h2>
                        <button
                            className="modal-close-btn"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="modal-tabs">
                        <button
                            className={`modal-tab ${activeTab === 'business' ? 'active' : ''}`}
                            onClick={() => setActiveTab('business')}
                        >
                            <i className="fas fa-building me-2"></i>
                            Details
                        </button>
                        <button
                            className={`modal-tab ${activeTab === 'content' ? 'active' : ''}`}
                            onClick={() => setActiveTab('content')}
                        >
                            <i className="fas fa-file-alt me-2"></i>
                            Content
                        </button>
                        <button
                            className={`modal-tab ${activeTab === 'ai-twilio' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ai-twilio')}
                        >
                            <i className="fas fa-robot me-2"></i>
                            AI
                        </button>
                        <button
                            className={`modal-tab ${activeTab === 'delivery' ? 'active' : ''}`}
                            onClick={() => setActiveTab('delivery')}
                        >
                            <i className="fas fa-truck me-2"></i>
                            Delivery
                        </button>
                        <button
                            className={`modal-tab ${activeTab === 'sms' ? 'active' : ''}`}
                            onClick={() => setActiveTab('sms')}
                        >
                            <i className="fas fa-sms me-2"></i>
                            SMS
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {message.text && (
                                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4 p-3 rounded-lg`}>
                                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
                                    {message.text}
                                </div>
                            )}

                            {/* Business Details Tab */}
                            {activeTab === 'business' && (
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Business Name *</label>
                                            <input
                                                type="text"
                                                name="business_name"
                                                className="form-control form-control-lg"
                                                value={formData.business_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Owner Name *</label>
                                            <input
                                                type="text"
                                                name="owner_name"
                                                className="form-control form-control-lg"
                                                value={formData.owner_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Business Type</label>
                                            <select
                                                name="business_type"
                                                className="form-select form-select-lg"
                                                value={formData.business_type}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Type</option>
                                                {businessTypes.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Business Slug/Reference</label>
                                            <input
                                                type="text"
                                                name="slug"
                                                className="form-control form-control-lg"
                                                value={formData.slug}
                                                onChange={handleChange}
                                                placeholder="my-business-name"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control form-control-lg"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Business Phone</label>
                                            <input
                                                type="tel"
                                                name="phone_number"
                                                className="form-control form-control-lg"
                                                value={formData.phone_number}
                                                onChange={handleChange}
                                                placeholder="+1234567890"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Emergency Contact</label>
                                            <input
                                                type="tel"
                                                name="emergency_contact"
                                                className="form-control form-control-lg"
                                                value={formData.emergency_contact}
                                                onChange={handleChange}
                                                placeholder="+1234567890"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Website</label>
                                            <input
                                                type="url"
                                                name="web_address"
                                                className="form-control form-control-lg"
                                                value={formData.web_address}
                                                onChange={handleChange}
                                                placeholder="https://example.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Business Address</label>
                                            <textarea
                                                name="address"
                                                className="form-control form-control-lg"
                                                value={formData.address}
                                                onChange={handleChange}
                                                rows="2"
                                                placeholder="Street address"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Content & Description Tab */}
                            {activeTab === 'content' && (
                                <div className="row g-4">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">Hero Text</label>
                                            <textarea
                                                name="hero_text"
                                                className="form-control form-control-lg"
                                                value={formData.hero_text}
                                                onChange={handleChange}
                                                rows="3"
                                                placeholder="Attention-grabbing headline for your homepage"
                                            />
                                            <div className="form-text mt-1">
                                                This appears as the main banner text on your homepage
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">About Us</label>
                                            <textarea
                                                name="description"
                                                className="form-control form-control-lg"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="10"
                                                placeholder="Tell customers about your business, your story, and what makes you special..."
                                            />
                                            <div className="form-text mt-1">
                                                This detailed description appears on your about page
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* AI & Twilio Tab */}
                            {activeTab === 'ai-twilio' && (
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-robot me-2"></i>
                                                AI Assistant Persona Name
                                            </label>
                                            <input
                                                type="text"
                                                name="ai_persona_name"
                                                className="form-control form-control-lg"
                                                value={formData.ai_persona_name}
                                                onChange={handleChange}
                                                placeholder="e.g., Assistant, Support Agent, etc."
                                            />
                                            <div className="form-text mt-1">
                                                What your AI assistant calls itself
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">AI Voice</label>
                                            <select
                                                name="ai_gender"
                                                className="form-select form-select-lg"
                                                value={formData.ai_gender}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Voice</option>
                                                <option key={'male'} value={'male'}>
                                                    Male
                                                </option>
                                                <option key={'female'} value={'female'}>
                                                    Female
                                                </option>
                                                <option key={'neutral'} value={'neutral'}>
                                                    Neutral
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-comment-dots me-2"></i>
                                                AI Tone
                                            </label>
                                            <select
                                                name="ai_tone"
                                                className="form-select form-select-lg"
                                                value={formData.ai_tone}
                                                onChange={handleChange}
                                            >
                                                {aiTones.map((tone) => (
                                                    <option key={tone} value={tone}>
                                                        {tone}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="form-text mt-1">
                                                Communication style of your AI assistant
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-sms me-2"></i>
                                                Twilio Number
                                            </label>
                                            <input
                                                type="tel"
                                                name="twillio_number"
                                                className="form-control form-control-lg"
                                                value={formData.twillio_number}
                                                onChange={handleChange}
                                                placeholder="+1234567890"
                                            />
                                            <div className="form-text mt-1">
                                                Phone number for SMS notifications
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Delivery & Collection Tab */}
                            {activeTab === 'delivery' && (
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="form-check form-switch mb-4 p-0">
                                            <div className="card border p-3 rounded-lg">
                                                <input
                                                    className="form-check-input ms-1 mb-2"
                                                    type="checkbox"
                                                    name="collection_allowed"
                                                    id="collection_allowed"
                                                    checked={formData.collection_allowed}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label fw-semibold" htmlFor="collection_allowed">
                                                    <i className="fas fa-store me-2"></i>
                                                    Allow Collection/Pickup
                                                </label>
                                                <div className="form-text mt-1">
                                                    Customers can collect orders from your location
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-check form-switch mb-4 p-0">
                                            <div className="card border p-3 rounded-lg">
                                                <input
                                                    className="form-check-input ms-1 mb-2"
                                                    type="checkbox"
                                                    name="delivery_allowed"
                                                    id="delivery_allowed"
                                                    checked={formData.delivery_allowed}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label fw-semibold" htmlFor="delivery_allowed">
                                                    <i className="fas fa-truck me-2"></i>
                                                    Allow Delivery
                                                </label>
                                                <div className="form-text mt-1">
                                                    Enable delivery service for orders
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-check form-switch mb-4 p-0">
                                            <div className="card border p-3 rounded-lg">
                                                <input
                                                    className="form-check-input ms-1 mb-2"
                                                    type="checkbox"
                                                    name="delivery_charge"
                                                    id="delivery_charge"
                                                    checked={formData.delivery_charge}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label fw-semibold" htmlFor="delivery_charge">
                                                    <i className="fas fa-money-bill-wave me-2"></i>
                                                    Charge for Delivery
                                                </label>
                                                <div className="form-text mt-1">
                                                    Add delivery charge to orders
                                                </div>
                                            </div>
                                        </div>

                                        {formData.delivery_charge && (
                                            <div className="mb-4">
                                                <div className="form-group">
                                                    <label className="form-label fw-semibold">Delivery Charge Price ($)</label>
                                                    <input
                                                        type="number"
                                                        name="charge_delivery_price"
                                                        className="form-control form-control-lg"
                                                        value={formData.charge_delivery_price}
                                                        onChange={handleChange}
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                    />
                                                    <div className="form-text mt-1">
                                                        Set to 0 for free delivery when enabled
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-12">
                                        <div className="border-top pt-4 mt-2">
                                            <h6 className="mb-3 fw-semibold">
                                                <i className="fas fa-receipt me-2"></i>
                                                Order Amount Settings
                                            </h6>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label fw-semibold">
                                                            Minimum Order Amount for Free Delivery ($)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="charge_apply_if_order_less_than_amount"
                                                            className="form-control form-control-lg"
                                                            value={formData.charge_apply_if_order_less_than_amount}
                                                            onChange={handleChange}
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="20.00"
                                                        />
                                                        <div className="form-text mt-1">
                                                            Delivery charges apply only if order is below this amount
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SMS Settings Tab */}
                            {activeTab === 'sms' && (
                                <div className="row g-4">
                                    <div className="col-12">
                                        <div className="alert alert-info mb-4">
                                            <i className="fas fa-info-circle me-2"></i>
                                            Configure which SMS notifications should be sent for different booking events.
                                        </div>
                                    </div>

                                    {/* Create Booking Notifications */}
                                    <div className="col-12">
                                        <div className="card border mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 fw-semibold">
                                                    <i className="fas fa-calendar-plus me-2"></i>
                                                    New Booking Creation
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_create_booking_owner"
                                                                id="send_sms_create_booking_owner"
                                                                checked={formData.send_sms_create_booking_owner}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_create_booking_owner">
                                                                Send SMS to Owner
                                                            </label>
                                                            <div className="form-text">Notify owner when a new booking is created</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_create_booking_customer"
                                                                id="send_sms_create_booking_customer"
                                                                checked={formData.send_sms_create_booking_customer}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_create_booking_customer">
                                                                Send SMS to Customer
                                                            </label>
                                                            <div className="form-text">Confirm booking creation to customer</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Accept Booking Notifications */}
                                    <div className="col-12">
                                        <div className="card border mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 fw-semibold">
                                                    <i className="fas fa-calendar-check me-2"></i>
                                                    Booking Acceptance
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_accept_booking_owner"
                                                                id="send_sms_accept_booking_owner"
                                                                checked={formData.send_sms_accept_booking_owner}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_accept_booking_owner">
                                                                Send SMS to Owner
                                                            </label>
                                                            <div className="form-text">Notify owner when booking is accepted</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_accept_booking_customer"
                                                                id="send_sms_accept_booking_customer"
                                                                checked={formData.send_sms_accept_booking_customer}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_accept_booking_customer">
                                                                Send SMS to Customer
                                                            </label>
                                                            <div className="form-text">Notify customer their booking is accepted</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Cancel Booking Notifications */}
                                    <div className="col-12">
                                        <div className="card border mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 fw-semibold">
                                                    <i className="fas fa-calendar-times me-2"></i>
                                                    Booking Cancellation
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_cancel_booking_owner"
                                                                id="send_sms_cancel_booking_owner"
                                                                checked={formData.send_sms_cancel_booking_owner}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_cancel_booking_owner">
                                                                Send SMS to Owner
                                                            </label>
                                                            <div className="form-text">Notify owner when booking is cancelled</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_cancel_booking_customer"
                                                                id="send_sms_cancel_booking_customer"
                                                                checked={formData.send_sms_cancel_booking_customer}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_cancel_booking_customer">
                                                                Send SMS to Customer
                                                            </label>
                                                            <div className="form-text">Notify customer about booking cancellation</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reschedule Booking Notifications */}
                                    <div className="col-12">
                                        <div className="card border mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 fw-semibold">
                                                    <i className="fas fa-calendar-alt me-2"></i>
                                                    Booking Rescheduling
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_reschedule_booking_owner"
                                                                id="send_sms_reschedule_booking_owner"
                                                                checked={formData.send_sms_reschedule_booking_owner}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_reschedule_booking_owner">
                                                                Send SMS to Owner
                                                            </label>
                                                            <div className="form-text">Notify owner when booking is rescheduled</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_reschedule_booking_customer"
                                                                id="send_sms_reschedule_booking_customer"
                                                                checked={formData.send_sms_reschedule_booking_customer}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_reschedule_booking_customer">
                                                                Send SMS to Customer
                                                            </label>
                                                            <div className="form-text">Notify customer about booking reschedule</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Complaint Receipt Notifications */}
                                    <div className="col-12">
                                        <div className="card border mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 fw-semibold">
                                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                                    Complaint Receipt
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_complaint_receipt_owner"
                                                                id="send_sms_complaint_receipt_owner"
                                                                checked={formData.send_sms_complaint_receipt_owner}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_complaint_receipt_owner">
                                                                Send SMS to Owner
                                                            </label>
                                                            <div className="form-text">Notify owner when a complaint is received</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-check form-switch mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="send_sms_complaint_receipt_customer"
                                                                id="send_sms_complaint_receipt_customer"
                                                                checked={formData.send_sms_complaint_receipt_customer}
                                                                onChange={handleChange}
                                                            />
                                                            <label className="form-check-label fw-medium" htmlFor="send_sms_complaint_receipt_customer">
                                                                Send SMS to Customer
                                                            </label>
                                                            <div className="form-text">Acknowledge complaint receipt to customer</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <div className="d-flex justify-content-between w-100 align-items-center">
                                <div className="tab-indicator">
                                    <small className="text-muted fw-medium">
                                        <i className={`fas fa-${activeTab === 'business' ? 'building' :
                                            activeTab === 'content' ? 'file-alt' :
                                                activeTab === 'ai-twilio' ? 'robot' :
                                                    activeTab === 'delivery' ? 'truck' : 'sms'
                                            } me-1`}></i>
                                        {activeTab === 'business' ? 'Business Details' :
                                            activeTab === 'content' ? 'Content & Description' :
                                                activeTab === 'ai-twilio' ? 'AI & Twilio' :
                                                    activeTab === 'delivery' ? 'Delivery & Collection' : 'SMS Settings'}
                                    </small>
                                </div>
                                <div className="d-flex gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4"
                                        onClick={onClose}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Settings'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SettingsPopup;